/* Copyright (c) 2020-present CloudIO Inc */

import { Filters, NewRow } from '@cloudio-saas/datasource-types';
import { getFilterFromActions } from 'actions/ActionQuery/ActionQuery';
import type {
  TriggerItemType,
  BaseActionItem,
  Item,
  ItemID,
  Trigger,
} from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import Store, { QueryProps } from 'store/Store';
import { isEmpty } from 'utils/isEmpty';

const invokeTrigger = async (
  trigger: Trigger | undefined,
  extraProps?: Record<string, unknown>,
): Promise<boolean> => {
  if (trigger) {
    return trigger(undefined, extraProps);
  }
  return true;
};

export const isTriggerOrActionEnabled = (
  item: BaseActionItem,
  platform: Platform,
  pageId: string,
  appUid: string,
) => {
  return platform.evalBoolean(
    appUid,
    pageId,
    item.itemId,
    'disableCondition',
    { pageId, itemId: item.itemId },
    item.props.disableCondition,
  );
};

const getActionSetItems = (
  triggerBeforeCreateItems: Item<TriggerItemType>[],
  platform: Platform,
  pageId: string,
  appUid: string,
): Item<'ActionSet'>[] => {
  let actionSetItems: Item<'ActionSet'>[] = [];
  triggerBeforeCreateItems.forEach((triggerBeforeCreateItem) => {
    if (
      platform.hasChildrenOfTypeById(
        appUid,
        pageId,
        triggerBeforeCreateItem.itemId,
        'ActionSet',
      )
    ) {
      const children = platform.getChildrenItemsById(
        appUid,
        pageId,
        triggerBeforeCreateItem.itemId,
        (child) =>
          child.type === 'ActionSet' &&
          isTriggerOrActionEnabled(child, platform, pageId, appUid),
      ) as Item<'ActionSet'>[];
      actionSetItems = [...actionSetItems, ...children];
    }
  });
  return actionSetItems;
};

const getActionSetValues = <T>(
  platform: Platform,
  appUid: string,
  pageId: string,
  itemId: ItemID | undefined,
  partialRecord: NewRow<T>,
) => {
  const actionSetValues = {};
  if (
    itemId &&
    platform.hasChildrenOfTypeById(
      appUid,
      pageId,
      itemId,
      'TriggerBeforeCreate',
    )
  ) {
    const triggerBeforeCreateItems = platform.getChildrenItemsById(
      appUid,
      pageId,
      itemId,
      (child) =>
        child.type === 'TriggerBeforeCreate' &&
        isTriggerOrActionEnabled(child, platform, pageId, appUid),
    ) as Item<TriggerItemType>[];
    const actionSetItems = getActionSetItems(
      triggerBeforeCreateItems,
      platform,
      pageId,
      appUid,
    );
    actionSetItems.forEach((actionSetItem: Item<'ActionSet'>) => {
      const { attribute, value } = actionSetItem.props;
      if (!isEmpty(attribute)) {
        const val =
          value.type === 'text'
            ? value.value
            : platform.evalUnknown(
                appUid,
                pageId,
                actionSetItem.itemId,
                'value',
                { new: partialRecord },
                value.value,
              );
        // @ts-ignore dynamic value
        actionSetValues[attribute] = val;
      }
    });
  }
  return actionSetValues;
};

const applyActionFilters = <T>(
  store: Store<T>,
  props: QueryProps<T>,
  filter: Filters<T>,
) => {
  if (!store.itemId) {
    return { props, filter };
  }
  const triggerBeforeQueryItems = store.platform.getChildrenItemsById(
    store.appUid,
    store.pageId,
    store.itemId,
    (child) =>
      (child.type === 'TriggerBeforeQuery' ||
        child.type === 'TriggerBeforeLoad') &&
      isTriggerOrActionEnabled(
        child,
        store.platform,
        store.pageId,
        store.appUid,
      ),
  ) as Item<'TriggerBeforeQuery'>[];

  if (triggerBeforeQueryItems.length) {
    const len = triggerBeforeQueryItems.length;
    for (let i = 0; i < len; i++) {
      const item = triggerBeforeQueryItems[i];
      const {
        aggregateList,
        data,
        fetchDistinct,
        filter: actionFilters,
        groupByAttributeCodeList,
        projection,
        sort,
        // eslint-disable-next-line no-await-in-loop
      } = getFilterFromActions({
        appUid: store.appUid,
        platform: store.platform,
        store,
        pageId: store.pageId,
        itemId: item.itemId,
        extraProps: {},
        showEmptyFilterWarning: false,
      });
      if (Object.keys(data).length) {
        props = {
          ...props,
          data: { ...(props.data ?? {}), ...data },
        };
      }
      if (actionFilters.length) {
        filter = [...filter, ...actionFilters];
      }
      if (aggregateList.length) {
        props = {
          ...props,
          aggregateList: [...(props.aggregateList ?? []), ...aggregateList],
        };
      }
      if (groupByAttributeCodeList.length) {
        props = {
          ...props,
          groupByAttributeCodeList: [
            ...(props.groupByAttributeCodeList ?? []),
            ...groupByAttributeCodeList,
          ],
        };
      }
      if (Object.keys(sort).length) {
        props = {
          ...props,
          sort: { ...(props.sort ?? {}), ...sort },
        };
      }
      if (Object.keys(projection).length) {
        props = {
          ...props,
          projection: { ...(props.projection ?? {}), ...projection },
        };
      }
      if (fetchDistinct) {
        props = {
          ...props,
          fetchDistinct,
        };
      }
    }
  }
  return { props, filter };
};

export { invokeTrigger, getActionSetValues, applyActionFilters };
